// A voicemail drop library panel (round 13): one workspace number's drops (Admin > Numbers) or the signed-in
// person's direct-line drops (Settings > My voicemail drops). Lists the drops in their order with the default
// marked; plays a saved drop inline; adds one by recording or uploading through the greeting recorder and WAV
// uploader; renames, reorders, sets the default and deletes with an in-page confirmation. The limit is shown.
import { type FormEvent, useContext, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { ChevronDown, ChevronUp, Pause, Pencil, Play, Star, Trash2, X } from 'lucide-react';
import { type VoicemailDropOwner, VoicemailDropsClient, isAbortError, isApiRequestError } from '../../lib/api.ts';
import { formatDuration } from '../../lib/format.ts';
import { Icon } from '../../ui/Icon.tsx';
import { SkeletonRows, StatePanel } from '../../ui/StatePanel.tsx';
import { useAnnouncer } from '../../ui/Announcer.tsx';
import { WorkspaceContext } from '../../state/workspace.tsx';
import { DropLibraryStore, type DropsApi, type LibrarySnapshot } from './dropLibrary.ts';
import { DROP_FILE_HINT, DROP_LEGAL_NOTE, DROP_LIMITS, type VoicemailDrop, type VoicemailFault, countLine, libraryPurpose, movedIndex, nameProblem, rememberedAudio } from './drops.ts';
import type { RecorderEnv } from './recorder.ts';
import { UploadRefusal, WavUploader } from './WavUploader.tsx';
import { VoicemailDropStyles } from './DropStyles.tsx';

export interface DropLibraryProps {
  owner: VoicemailDropOwner;
  /** "Voicemail drops for Brandcore Sales", "My voicemail drops". */
  title: string;
  headingLevel?: 2 | 3;
  /** From the role mirror; the server's 403 still turns changes off. */
  canManage: boolean;
  /** Injected in tests and the preview; the workspace's API otherwise. */
  client?: DropsApi & Pick<VoicemailDropsClient, 'audio'>;
  env?: RecorderEnv;
}

export function DropLibrary(props: DropLibraryProps) {
  // Admin renders without the workspace in some tests; the panel then needs an injected client.
  const api = useContext(WorkspaceContext)?.api ?? null;
  const client = useMemo(() => props.client ?? (api ? new VoicemailDropsClient(api) : null), [props.client, api]);
  if (!client) return null;
  return <ConnectedLibrary {...props} client={client} />;
}

function ConnectedLibrary(props: DropLibraryProps & { client: DropsApi & Pick<VoicemailDropsClient, 'audio'> }) {
  const { client } = props;
  const key = props.owner.scope === 'number' ? props.owner.numberId : props.owner.userId;
  const store = useMemo(() => new DropLibraryStore(client, props.owner), [client, props.owner.scope, key]);
  const snap = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  useEffect(() => {
    void store.load();
    return () => store.dispose();
  }, [store]);
  return <DropLibraryView {...props} store={store} snap={snap} audio={client} />;
}

type StoreActions = Pick<DropLibraryStore, 'load' | 'add' | 'rename' | 'move' | 'makeDefault' | 'remove'>;

/** The panel for one decided snapshot (exported for tests). */
export function DropLibraryView({ owner, title, headingLevel = 2, canManage: roleCanManage, env, store, snap, audio }: DropLibraryProps & { store: StoreActions; snap: LibrarySnapshot; audio: Pick<VoicemailDropsClient, 'audio'> | null }) {
  const uid = useId().replace(/:/g, '');
  const { announce } = useAnnouncer();
  const [saved, setSaved] = useState<string | null>(null);
  const canManage = roleCanManage && !snap.manageDenied;
  const H = headingLevel === 3 ? 'h3' : 'h2';
  const atLimit = snap.drops.length >= snap.limit;
  const say = (text: string) => { setSaved(text); announce(text); };

  const body = () => {
    if (snap.status === 'idle' || (snap.status === 'loading' && snap.drops.length === 0)) return <SkeletonRows rows={2} label="Loading voicemail drops" />;
    if (snap.status === 'error' && snap.error && snap.drops.length === 0) {
      if (snap.missing) return <StatePanel variant="unavailable" compact headingLevel={3} title="Voicemail drops aren’t available here yet" body="This server doesn’t offer voicemail drops for this line yet. Calls aren’t affected. The library appears here once it does." details={{ code: snap.error.code, requestId: snap.error.requestId }} />;
      if (snap.denied) return <StatePanel variant="denied" compact headingLevel={3} title="Your role can’t view this library" body={owner.scope === 'number' ? 'Number libraries are managed by admins and owners.' : 'Ask an admin if you need voicemail drops.'} details={{ permission: owner.scope === 'number' ? 'voicemail_drops:manage_all' : 'voicemail_drops:manage_own', code: snap.error.code, requestId: snap.error.requestId }} />;
      return <StatePanel variant={snap.error.code === 'network_error' ? 'offline' : 'error'} compact headingLevel={3} title={snap.error.title} action={<button type="button" className="btn" onClick={() => void store.load()}>Try again</button>} details={{ code: snap.error.code, requestId: snap.error.requestId }} />;
    }
    if (snap.drops.length === 0) {
      return <p className="field-hint">{owner.scope === 'number' ? 'No drops for this number yet. Agents calling from it see only their own drops.' : 'No drops yet. Add one below, then use it from the phone panel on any call you place.'}</p>;
    }
    return (
      <ol className="vmd-list" aria-label={`${title}, in the order agents see them`}>
        {snap.drops.map((drop, i) => (
          <DropRow key={drop.id} drop={drop} index={i} count={snap.drops.length} library={snap.drops} busy={snap.busy.has(drop.id)} canManage={canManage} store={store} audio={audio} onSaved={say} />
        ))}
      </ol>
    );
  };

  const ready = snap.status === 'ready' || snap.drops.length > 0;
  return (
    <section className="vmd-library" aria-labelledby={`${uid}-title`}>
      <VoicemailDropStyles />
      <div className="vmd-library-head">
        <H id={`${uid}-title`} className="section-title">{title}</H>
        {ready ? <span className={`chip vmd-count${atLimit ? ' tone-warn' : ''}`}>{countLine(snap.drops.length, snap.limit)}</span> : null}
      </div>
      <p className="meta">{libraryPurpose(owner.scope)}</p>
      {saved ? (
        <div className="vmd-saved" role="status">
          <p>{saved}</p>
          <button type="button" className="btn btn-quiet btn-icon" aria-label="Dismiss message" onClick={() => setSaved(null)}><Icon icon={X} /></button>
        </div>
      ) : null}
      {snap.status === 'error' && snap.error && snap.drops.length > 0 ? <p className="inline-reason" role="alert">{snap.error.title} Showing the last list.</p> : null}
      {body()}
      {snap.skipped > 0 ? <p className="meta">{snap.skipped} drop{snap.skipped === 1 ? '' : 's'} couldn’t be shown in this version.</p> : null}
      {ready && !snap.missing ? (
        canManage ? (
          atLimit ? (
            <p className="inline-reason">This library has {snap.limit} drops, the most it can hold. Delete one to add another.</p>
          ) : (
            <AddDrop key={snap.drops.length} library={snap.drops} env={env} onAdd={async (name, wav) => {
              const result = await store.add(name, wav);
              if (result.ok) say(snap.drops.length === 0 ? `“${result.value.name}” added. It’s the default for now.` : `“${result.value.name}” added at the end of the list.`);
              return result.ok ? { ok: true } : { ok: false, fault: result.fault };
            }} />
          )
        ) : (
          <p className="field-hint">{snap.manageDenied ? 'The server refused a change: your role can’t manage this library.' : owner.scope === 'number' ? 'Your role can’t change this number’s drops. Admins and owners can.' : 'Your role can’t change voicemail drops.'}</p>
        )
      ) : null}
      <p className="meta">{DROP_LEGAL_NOTE}</p>
    </section>
  );
}

function DropRow({ drop, index, count, library, busy, canManage, store, audio, onSaved }: { drop: VoicemailDrop; index: number; count: number; library: readonly VoicemailDrop[]; busy: boolean; canManage: boolean; store: StoreActions; audio: Pick<VoicemailDropsClient, 'audio'> | null; onSaved: (text: string) => void }) {
  const uid = useId().replace(/:/g, '');
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(drop.name);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [fault, setFault] = useState<VoicemailFault | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const renameRef = useRef<HTMLButtonElement>(null);
  const deleteRef = useRef<HTMLButtonElement>(null);
  const keepRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (renaming) nameRef.current?.select(); }, [renaming]);
  useEffect(() => { if (confirming) keepRef.current?.focus(); }, [confirming]);

  const run = async (work: () => Promise<{ ok: true } | { ok: false; fault: VoicemailFault }>, done: string) => {
    setFault(null);
    const result = await work();
    if (result.ok) onSaved(done);
    else setFault(result.fault);
    return result.ok;
  };
  const submitRename = async (e: FormEvent) => {
    e.preventDefault();
    const why = nameProblem(name, library, drop.id);
    setProblem(why);
    if (why) { nameRef.current?.focus(); return; }
    if (name.replace(/\s+/g, ' ').trim() === drop.name) { setRenaming(false); renameRef.current?.focus(); return; }
    if (await run(() => store.rename(drop, name), `Renamed to “${name.replace(/\s+/g, ' ').trim()}”.`)) { setRenaming(false); setTimeout(() => renameRef.current?.focus(), 0); }
  };

  return (
    <li className={`vmd-row${drop.is_default ? ' is-default' : ''}`} aria-busy={busy || undefined}>
      <div className="vmd-row-main">
        <span className="vmd-pos num" aria-hidden="true">{index + 1}</span>
        <span className="vmd-name">{drop.name}</span>
        {drop.is_default ? <span className="row-badge vmd-default"><Icon icon={Star} size="1em" /> Default</span> : null}
        <span className="vmd-length num"><span className="visually-hidden">Length </span>{formatDuration(drop.duration_ms)}</span>
      </div>
      <div className="vmd-row-actions" role="group" aria-label={`Actions for ${drop.name}`}>
        <SavedDropPreview drop={drop} audio={audio} />
        {canManage ? (
          <>
            <button ref={renameRef} type="button" className="btn btn-small btn-quiet" onClick={() => { setName(drop.name); setProblem(null); setRenaming(true); setConfirming(false); }} disabled={busy} aria-label={`Rename ${drop.name}`}><Icon icon={Pencil} size="1em" /> Rename</button>
            {!drop.is_default ? (
              <>
                <button type="button" className="btn btn-small btn-quiet btn-icon vmd-move" onClick={() => void run(() => store.move(drop, -1), `“${drop.name}” moved up to place ${index}.`)} disabled={busy || movedIndex(library, drop.id, -1) === null} aria-label={`Move ${drop.name} up`} title={movedIndex(library, drop.id, -1) === null && index > 0 ? 'The default stays first' : 'Move up'}><Icon icon={ChevronUp} size="1em" /></button>
                <button type="button" className="btn btn-small btn-quiet btn-icon vmd-move" onClick={() => void run(() => store.move(drop, 1), `“${drop.name}” moved down to place ${index + 2}.`)} disabled={busy || movedIndex(library, drop.id, 1) === null} aria-label={`Move ${drop.name} down`} title="Move down"><Icon icon={ChevronDown} size="1em" /></button>
              </>
            ) : null}
            {!drop.is_default ? <button type="button" className="btn btn-small" onClick={() => void run(() => store.makeDefault(drop), `“${drop.name}” is now the default.`)} disabled={busy} aria-label={`Make ${drop.name} the default`}><Icon icon={Star} size="1em" /> Make default</button> : null}
            <button ref={deleteRef} type="button" className="btn btn-small btn-quiet vmd-danger" onClick={() => { setFault(null); setRenaming(false); setConfirming(true); }} disabled={busy} aria-label={`Delete ${drop.name}`}><Icon icon={Trash2} size="1em" /> Delete</button>
          </>
        ) : null}
      </div>
      {renaming ? (
        <form className="vmd-rename" onSubmit={e => void submitRename(e)} noValidate aria-label={`Rename ${drop.name}`}>
          <div className="field">
            <label htmlFor={`${uid}-name`}>New name</label>
            <input ref={nameRef} id={`${uid}-name`} value={name} maxLength={DROP_LIMITS.nameMax + 20} onChange={e => setName(e.target.value)} aria-invalid={problem ? true : undefined} aria-describedby={problem ? `${uid}-problem` : undefined} onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); setRenaming(false); renameRef.current?.focus(); } }} />
          </div>
          {problem ? <p id={`${uid}-problem`} className="field-error" role="alert">{problem}</p> : null}
          <div className="form-actions">
            <button type="button" className="btn btn-quiet" onClick={() => { setRenaming(false); renameRef.current?.focus(); }} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>{busy ? 'Saving…' : 'Save name'}</button>
          </div>
        </form>
      ) : null}
      {confirming ? (
        <div className="vmd-confirm" role="alertdialog" aria-labelledby={`${uid}-confirm`} aria-describedby={`${uid}-confirm-why`}>
          <p id={`${uid}-confirm`}><strong>Delete “{drop.name}”?</strong></p>
          <p id={`${uid}-confirm-why`}>{drop.is_default ? (count > 1 ? 'It’s the default. The next drop in the list becomes the default.' : 'It’s the only drop here, so this library will have none.') : 'Agents won’t be able to pick it any more.'} Calls already dropped keep its name in their history.</p>
          <div className="form-actions">
            <button ref={keepRef} type="button" className="btn" onClick={() => { setConfirming(false); deleteRef.current?.focus(); }} disabled={busy}>Keep it</button>
            <button type="button" className="btn btn-end" disabled={busy} aria-busy={busy || undefined} onClick={() => void run(() => store.remove(drop), `“${drop.name}” deleted.`).then(ok => { if (ok) setConfirming(false); })}>{busy ? 'Deleting…' : 'Delete'}</button>
          </div>
        </div>
      ) : null}
      {fault ? <UploadRefusal fault={fault} /> : null}
    </li>
  );
}

type PreviewState = { status: 'idle' | 'loading' | 'playing' | 'paused' } | { status: 'error'; text: string };

/** Listen to a saved drop: the audio is fetched once on the first press and kept for this page. */
export function SavedDropPreview({ drop, audio }: { drop: Pick<VoicemailDrop, 'id' | 'name'>; audio: Pick<VoicemailDropsClient, 'audio'> | null }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<PreviewState>({ status: 'idle' });
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => {
    abort.current?.abort();
  }, []);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const local = rememberedAudio(drop.id);
  const toggle = async () => {
    const el = audioRef.current;
    if (!el || (!audio && !local)) return;
    if (url) {
      if (el.paused) void el.play().catch(() => setState({ status: 'paused' }));
      else el.pause();
      return;
    }
    setState({ status: 'loading' });
    abort.current = new AbortController();
    try {
      // A drop added from this tab plays from memory; any other is fetched once.
      const blob = local ? new Blob([local], { type: 'audio/wav' }) : await (await audio!.audio(drop.id, abort.current.signal)).blob();
      const next = URL.createObjectURL(blob);
      setUrl(next);
      el.src = next;
      await el.play().catch(() => setState({ status: 'paused' }));
    } catch (error) {
      if (isAbortError(error)) return;
      const status = isApiRequestError(error) ? error.status : 0;
      setState({ status: 'error', text: status === 404 || status === 405 || status === 501 ? 'Listening to saved drops isn’t available on this server yet.' : status === 403 ? 'Your role can’t listen to this drop.' : 'Couldn’t load the audio. Try again.' });
    }
  };
  const playing = state.status === 'playing';
  return (
    <>
      <audio ref={audioRef} preload="none" onPlay={() => setState({ status: 'playing' })} onPause={() => setState({ status: 'paused' })} onEnded={() => setState({ status: 'paused' })} />
      <button type="button" className="btn btn-small" onClick={() => void toggle()} disabled={(!audio && !local) || state.status === 'loading'} aria-busy={state.status === 'loading' || undefined} aria-label={`${playing ? 'Pause' : 'Listen to'} ${drop.name}`}>
        <Icon icon={playing ? Pause : Play} size="1em" /> {state.status === 'loading' ? 'Loading…' : playing ? 'Pause' : 'Listen'}
      </button>
      {state.status === 'error' ? <span className="inline-reason vmd-preview-error" role="status">{state.text}</span> : null}
    </>
  );
}

/** Add a drop: its name, then the shared recorder and uploader (nothing is sent until Upload). */
function AddDrop({ library, env, onAdd }: { library: readonly VoicemailDrop[]; env: RecorderEnv | undefined; onAdd: (name: string, wav: Uint8Array<ArrayBuffer>) => Promise<{ ok: true } | { ok: false; fault: VoicemailFault | null }> }) {
  const uid = useId().replace(/:/g, '');
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const copy = { legend: 'Add a voicemail drop', noun: 'voicemail drop', uploadLabel: 'Add drop', fileHint: DROP_FILE_HINT, prepareCopy: { greeting_too_short: 'A voicemail drop must be at least 1 second long.', greeting_too_long: 'A voicemail drop can be at most 60 seconds long. Shorter is safer: some machines stop recording early.', greeting_too_large: 'A voicemail drop file can be at most 1 MiB.' } };
  return (
    <WavUploader copy={copy} env={env} onUpload={async wav => {
      const why = nameProblem(name, library);
      setProblem(why);
      if (why) { nameRef.current?.focus(); return { ok: false, fault: null }; }
      return onAdd(name, wav);
    }}>
      <div className="field">
        <label htmlFor={`${uid}-name`}>Name</label>
        <input ref={nameRef} id={`${uid}-name`} value={name} maxLength={DROP_LIMITS.nameMax + 20} onChange={e => { setName(e.target.value); if (problem) setProblem(nameProblem(e.target.value, library)); }} aria-invalid={problem ? true : undefined} aria-describedby={`${uid}-name-hint${problem ? ` ${uid}-name-problem` : ''}`} autoComplete="off" />
        <p id={`${uid}-name-hint`} className="field-hint">Agents pick by name, so say what it’s for: “Quote follow-up”, “Missed demo”. Up to {DROP_LIMITS.nameMax} characters.</p>
        {problem ? <p id={`${uid}-name-problem`} className="field-error" role="alert">{problem}</p> : null}
      </div>
    </WavUploader>
  );
}
